'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface NetZeroRoadmapProps {
  currentLanguage: string;
}

const NetZeroRoadmap = ({ currentLanguage }: NetZeroRoadmapProps) => {
  const [selectedMilestone, setSelectedMilestone] = useState<string>('2030');

  const milestones = [
    {
      year: '2030',
      title: 'Interim Target',
      subtitle: 'Short-term Emission Reductions',
      progress: 45,
      goals: [
        { name: 'Scope 1 & 2 Reduction', target: '50%', current: '28%', status: 'in-progress' },
        { name: 'Renewable Energy Mix', target: '60%', current: '42%', status: 'in-progress' },
        { name: 'Energy Efficiency', target: '30%', current: '18%', status: 'in-progress' },
        { name: 'Supplier Engagement', target: '80%', current: '55%', status: 'in-progress' },
      ],
      initiatives: [
        'Solar power installation across facilities',
        'Electric vehicle fleet transition',
        'Green building certifications',
        'Supplier sustainability training',
      ],
      color: 'success',
    },
    {
      year: '2047',
      title: 'Mid-term Target',
      subtitle: 'Substantial Transformation',
      progress: 20,
      goals: [
        { name: 'Scope 1, 2 & 3 Reduction', target: '75%', current: '15%', status: 'planned' },
        { name: '100% Renewable Energy', target: '100%', current: '42%', status: 'planned' },
        { name: 'Circular Economy', target: '90%', current: '25%', status: 'planned' },
        { name: 'Value Chain Alignment', target: '100%', current: '35%', status: 'planned' },
      ],
      initiatives: [
        'Carbon capture and storage implementation',
        'Hydrogen fuel infrastructure',
        'Circular economy business models',
        'Nature-based solutions investment',
      ],
      color: 'primary',
    },
    {
      year: '2074',
      title: 'Long-term Vision',
      subtitle: 'Net Zero Achievement',
      progress: 5,
      goals: [
        { name: 'Net Zero Emissions', target: '100%', current: '5%', status: 'vision' },
        { name: 'Climate Positive', target: 'Carbon Negative', current: 'Planning', status: 'vision' },
        { name: 'Biodiversity Net Positive', target: '100%', current: '10%', status: 'vision' },
        { name: 'Regenerative Systems', target: 'Full Integration', current: 'Research', status: 'vision' },
      ],
      initiatives: [
        'Advanced carbon removal technologies',
        'Ecosystem restoration programs',
        'Climate adaptation infrastructure',
        'Science-based innovation hubs',
      ],
      color: 'info',
    },
  ];

  const selectedData = milestones.find((m) => m.year === selectedMilestone) || milestones[0];

  return (
    <section className="py-20 bg-gradient-to-br from-surface to-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary mb-4">
            NetZero Roadmap Visualization
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            Track your progress toward 2030, 2047, and 2074 climate commitments with milestone-based planning
          </p>
        </div>

        {/* Timeline Selector */}
        <div className="flex flex-col md:flex-row items-center justify-center gap-4 mb-12">
          {milestones.map((milestone) => (
            <button
              key={milestone.year}
              onClick={() => setSelectedMilestone(milestone.year)}
              className={`w-full md:w-auto px-8 py-6 rounded-xl border-2 transition-all duration-300 ${
                selectedMilestone === milestone.year
                  ? `border-${milestone.color} bg-${milestone.color}/10 shadow-natural-lg`
                  : 'border-border bg-card hover:border-primary/50'
              }`}
            >
              <div className="text-center">
                <div className={`text-3xl font-heading font-bold text-${milestone.color} mb-2`}>
                  {milestone.year}
                </div>
                <div className="text-sm font-semibold text-text-primary mb-1">{milestone.title}</div>
                <div className="text-xs text-text-secondary">{milestone.subtitle}</div>
                <div className="mt-3">
                  <div className="w-full bg-border rounded-full h-2 overflow-hidden">
                    <div
                      className={`bg-${milestone.color} h-full rounded-full transition-all duration-500`}
                      style={{ width: `${milestone.progress}%` }}
                    />
                  </div>
                  <div className="text-xs text-text-secondary mt-1">{milestone.progress}% Progress</div>
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Selected Milestone Details */}
        <div className="bg-card border-2 border-primary rounded-2xl p-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h3 className="font-heading font-bold text-2xl text-text-primary mb-2">
                {selectedData.year} Goals & Progress
              </h3>
              <p className="text-text-secondary">{selectedData.subtitle}</p>
            </div>
            <div className={`text-5xl font-heading font-bold text-${selectedData.color}`}>
              {selectedData.progress}%
            </div>
          </div>

          {/* Goals Progress */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            {selectedData.goals.map((goal, index) => (
              <div key={index} className="bg-surface border border-border rounded-lg p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-grow">
                    <h4 className="font-semibold text-text-primary mb-2">{goal.name}</h4>
                    <div className="flex items-center space-x-4 text-sm">
                      <span className="text-text-secondary">
                        Current: <span className="font-semibold text-primary">{goal.current}</span>
                      </span>
                      <span className="text-text-secondary">
                        Target: <span className="font-semibold text-success">{goal.target}</span>
                      </span>
                    </div>
                  </div>
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      goal.status === 'in-progress' ?'bg-warning/10 text-warning'
                        : goal.status === 'planned' ?'bg-info/10 text-info' :'bg-secondary/10 text-secondary'
                    }`}
                  >
                    {goal.status}
                  </span>
                </div>
                <div className="w-full bg-border rounded-full h-2.5 overflow-hidden">
                  <div
                    className={`bg-${selectedData.color} h-full rounded-full transition-all duration-700`}
                    style={{
                      width: goal.status === 'in-progress' ? '60%' : goal.status === 'planned' ? '30%' : '10%',
                    }}
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Key Initiatives */}
          <div>
            <h4 className="font-heading font-bold text-lg text-text-primary mb-4">Key Initiatives</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {selectedData.initiatives.map((initiative, index) => (
                <div key={index} className="flex items-start space-x-3 bg-surface/50 rounded-lg p-4">
                  <Icon name="RocketLaunchIcon" size={20} className={`text-${selectedData.color} mt-0.5`} />
                  <span className="text-sm text-text-primary">{initiative}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Visual Timeline */}
        <div className="mt-12 bg-surface border border-border rounded-xl p-8">
          <h4 className="font-heading font-bold text-xl text-text-primary mb-6 text-center">
            Complete Roadmap Timeline
          </h4>
          <div className="relative">
            <div className="absolute top-6 left-0 right-0 h-1 bg-gradient-to-r from-success via-primary to-info" />
            <div className="relative flex justify-between">
              {milestones.map((milestone, index) => (
                <div key={index} className="flex flex-col items-center">
                  <div
                    className={`w-12 h-12 rounded-full bg-${milestone.color} flex items-center justify-center text-white font-bold shadow-natural mb-3`}
                  >
                    {milestone.year.slice(2)}
                  </div>
                  <div className="text-center">
                    <div className="text-sm font-semibold text-text-primary">{milestone.title}</div>
                    <div className="text-xs text-text-secondary">{milestone.progress}%</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default NetZeroRoadmap;